import { combine, createEvent, createStore, Event, EventCallable, restore, sample } from 'effector';
import { modelFactory } from 'effector-factorio';
import { createGate } from 'effector-react';

import { searchEmployeeFx } from 'shared/api';
import { definitions } from 'shared/types/APISchema';

import { debounce } from 'patronum';

export const minSearchLength = 2;

type FactoryOptions = {
  onSelectUser?: EventCallable<definitions['EmployeeSearch']>;
  openSearchPanel?: Event<boolean>;
  onSelectTeam?: EventCallable<definitions['MinTeam']>;
  alwaysVisible?: boolean;
};

export const factory = modelFactory(({ onSelectUser, openSearchPanel, alwaysVisible, onSelectTeam }: FactoryOptions) => {
  const SearchGate = createGate();

  const reset = createEvent();

  const selectUser = createEvent<definitions['EmployeeSearch']>();
  const selectTeam = createEvent<definitions['MinTeam']>();

  const $searchIsOpen = createStore(alwaysVisible ?? false).reset(reset);

  if (openSearchPanel) {
    $searchIsOpen.on(openSearchPanel, (_, val) => val);
  }

  const changeSearchValue = createEvent<string>();

  const $searchValue = restore(changeSearchValue, '').reset(SearchGate.close).reset(reset);

  const $debouncedSearchValue = restore(
    debounce({
      source: $searchValue,
      timeout: 300,
    }),
    ''
  );

  const $isNeedShowSkeleton = combine(searchEmployeeFx.pending, $searchValue, (pending, value) => pending && value.length > 0);

  const $foundUsers = restore<definitions['GenericSearch']>(searchEmployeeFx.doneData, null)
    .on($searchValue, (state, payload) => (payload.length === 0 ? null : state))
    .on($searchIsOpen, (state, payload) => (payload ? state : null))
    .reset(SearchGate.close)
    .reset(reset);

  const $isEmptyShow = combine(
    $foundUsers,
    $debouncedSearchValue,
    (users, value) =>
      users?.activeEmployees?.length === 0 &&
      users?.teams?.length === 0 &&
      users?.notActiveEmployees?.length === 0 &&
      value.length > minSearchLength
  );

  sample({
    source: $debouncedSearchValue,
    fn: (value) => ({ query: value }),
    filter: (debouncedSearchValue) => debouncedSearchValue.length > minSearchLength,
    target: searchEmployeeFx,
  });

  // sample({
  //   clock: [selectUser, selectTeam],
  //   target: SearchGate.close,
  // });

  sample({
    clock: [selectUser, selectTeam],
    target: reset,
  });

  if (onSelectUser) {
    sample({
      clock: selectUser,
      target: onSelectUser,
    });
  }

  if (onSelectTeam) {
    sample({
      clock: selectTeam,
      target: onSelectTeam,
    });
  }

  return {
    $isNeedShowSkeleton,
    $isEmptyShow,
    reset,
    selectUser,
    selectTeam,
    handleSearchOpen: openSearchPanel,
    $searchIsOpen,
    SearchGate,
    changeSearchValue,
    $searchValue,
    $foundUsers,
    $debouncedSearchValue,
  };
});
